import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface CodeBlockProps {
  children: string;
  className?: string;
  language?: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ children, className, language }) => {
  // Extract language from className (e.g., "language-python" -> "python")
  const match = /language-(\w+)/.exec(className || '');
  const lang = language || (match ? match[1] : '');

  // Use dark mode detection (you can customize this based on your theme system)
  const isDarkMode = document.documentElement.classList.contains('dark');
  const style = isDarkMode ? vscDarkPlus : vs;

  if (lang) {
    return (
      <SyntaxHighlighter
        language={lang}
        style={style}
        customStyle={{
          margin: '1.5rem 0',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          lineHeight: '1.5',
        }}
        showLineNumbers={lang === 'python' || lang === 'javascript' || lang === 'typescript'}
      >
        {children.trim()}
      </SyntaxHighlighter>
    );
  }

  // Fallback for inline code or code without language
  return (
    <code className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1 rounded text-sm font-mono">
      {children}
    </code>
  );
};

interface PreProps {
  children: React.ReactElement<CodeBlockProps>;
}

export const Pre: React.FC<PreProps> = ({ children }) => {
  // If it's a code block, render with syntax highlighting
  if (React.isValidElement(children) && children.props.className) {
    return <CodeBlock {...children.props} />;
  }

  // Fallback for regular pre blocks
  return (
    <pre className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
      {children}
    </pre>
  );
};